'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import '@splidejs/splide/dist/css/splide.min.css';
import SplideCore from '@splidejs/splide';
import * as AutoScroll from '@splidejs/splide-extension-auto-scroll';
import confetiAnimation from '../../assets/confetti.json';
import LottieAnimation from '../../hooks/getLottie';
import { cargarProductosPorMarca } from '../../utils/loaders';

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const summarizeText = (value, maxLength = 110) => {
  const parsed = stripHtml(value);
  if (!parsed) return 'Consulta disponibilidad y especificaciones técnicas.';
  if (parsed.length <= maxLength) return parsed;
  return `${parsed.slice(0, maxLength).trimEnd()}...`;
};

export default function CarruselProductos({ tipo }) {
  const splideRef = useRef();
  const splideInstance = useRef();
  const [productos, setProductos] = useState([]);

  useEffect(() => {
    const getProductos = async () => {
      const res = await cargarProductosPorMarca(tipo);
      setProductos(res.data);
    };
    getProductos();
  }, [tipo]);

  // Updates Splide when the component mounts and when 'productos' changes
  useEffect(() => {
    if (splideRef.current && productos.length > 0) {
      if (splideInstance.current) {
        splideInstance.current.destroy();
      }

      splideInstance.current = new SplideCore(splideRef.current, {
        type: 'loop',
        focus: 'center',
        perPage: 5,
        gap: '1.75rem',
        autoScroll: {
          speed: 0.8,
        },
        breakpoints: {
          480: {
            perPage: 2,
            gap: '0.75rem',
          },
          640: {
            perPage: 2,
            gap: '0.75rem',
          },
          1024: {
            perPage: 3,
            gap: '1rem',
          },
        },
      });

      splideInstance.current.mount(AutoScroll);
    }

    return () => {
      if (splideInstance.current) {
        splideInstance.current.destroy();
      }
    };
  }, [productos]);

  return (
    <div className="pb-8 md:pb-10 max-w-7xl mx-auto overflow-hidden">
      {/* "Ver todos" alineado a la derecha */}
      <div className="px-4 flex justify-end mb-1">
        <Link
          to="/productos"
          className="inline-flex items-center gap-1.5 text-[#00BEE7] font-bold text-base hover:underline"
        >
          Ver todos
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      <div className="splide relative z-10" ref={splideRef}>
        <div className="splide__track py-4 md:py-6 px-4">
          <ul className="splide__list">
            {productos.map((producto) => (
              <Link
                to={`/producto/${producto.titulo}`}
                state={{ id: producto.id }}
                className="splide__slide pb-4 h-[252px] md:h-[294px]"
                key={producto.id}
              >
                <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_24px_-18px_rgba(15,39,75,0.7)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00BEE7]/60 hover:shadow-[0_18px_30px_-18px_rgba(0,190,231,0.55)]">
                  <div className="relative h-[62%] overflow-hidden bg-gradient-to-b from-slate-50 to-white p-3 md:p-4">
                    <img
                      src={producto.imagen_url}
                      alt={producto.titulo}
                      width={240}
                      height={240}
                      className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                      onError={(e) => { e.currentTarget.src = 'https://dps2wxsijib8x.cloudfront.net/atst/LOGO-ATST.png'; e.currentTarget.onerror = null; }}
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-slate-200/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    {producto.estado === 'Proximamente' && (
                      <>
                        <div className="absolute right-0 top-2 z-10 rounded-l border border-emerald-300 bg-emerald-100 px-2 py-0.5">
                          <h3 className="uppercase text-emerald-600 font-bold text-xs">
                            {producto.estado}
                          </h3>
                        </div>
                        <div className="absolute inset-0 w-full h-full z-0">
                          <LottieAnimation animationData={confetiAnimation} />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex h-[38%] flex-col justify-between border-t border-slate-100 bg-[#0f274b] px-3 py-2.5 text-white transition-colors duration-300 group-hover:bg-[#0e355f]">
                    <h3 className="text-sm md:text-base font-extrabold leading-tight tracking-tight">
                      {producto.titulo}
                    </h3>
                    <p
                      className="text-[11px] md:text-xs text-blue-100/95 leading-snug"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {summarizeText(producto.descripcion)}
                    </p>
                    <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-[#00d0f3]">
                      Ver ficha
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </ul>
        </div>
      </div>

      {/* Botón mobile */}
      <div className="md:hidden w-full relative z-10 flex justify-center mt-2 px-4">
        <Link
          to="/productos"
          className="w-full text-center bg-[#0f274b] hover:bg-[#00BEE7] text-white hover:text-[#0f274b] rounded-xl py-3 px-4 font-extrabold text-sm transition-all duration-200 shadow"
        >
          VER TODOS LOS PRODUCTOS
        </Link>
      </div>
    </div>
  );
}

CarruselProductos.propTypes = {
  tipo: PropTypes.string.isRequired,
};
